'use client';

import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { createClient } from '../lib/supabase/client';
import { useRouter } from 'next/navigation';

export type HrmsRole =
  | 'super_admin' | 'hr_admin' | 'hr_manager' | 'hr_officer' | 'dept_manager' | 'team_leader' | 'employee' | 'executive';

export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  hrms_role: HrmsRole;
  avatar_url?: string | null;
  organization_id?: string | null;
  status: string;
  is_active: boolean;
  must_change_password: boolean;
  last_login_at?: string | null;
  department_id?: string | null;
  position_id?: string | null;
  employee_id?: string | null;
  employee_record_id?: string | null;
  phone?: string | null;
}

interface AuthContextType {
  user: any;
  session: any;
  profile: UserProfile | null;
  loading: boolean;
  permissions: Set<string>;
  signIn: (email: string, password: string) => Promise<any>;
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<any>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  refreshProfile: () => Promise<void>;
  hasRole: (roles: HrmsRole | HrmsRole[]) => boolean;
  canAccess: (minRole: HrmsRole) => boolean;
  hasPermission: (module: string, resource: string, action: string) => boolean;
}

const ROLE_HIERARCHY: Record<HrmsRole, number> = {
  super_admin: 100,
  executive: 80,
  hr_admin: 70,
  hr_manager: 70,
  hr_officer: 60,
  dept_manager: 50,
  team_leader: 40,
  employee: 10,
};

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const supabase = createClient();
  const router = useRouter();
  // Prevent duplicate fetches on rapid auth state changes
  const fetchingRef = useRef(false);

  const fetchProfile = useCallback(async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, email, full_name, hrms_role, avatar_url, organization_id, status, is_active, must_change_password, last_login_at, department_id, position_id, employee_id, employee_record_id, phone')
        .eq('id', userId)
        .maybeSingle();
      if (error) {
        console.log('Profile fetch error:', error.message);
        return null;
      }
      // Only resolve employee_record_id if missing — fire-and-forget, don't block render
      if (data && !data.employee_record_id) {
        supabase.rpc('get_employee_id_for_user', { p_user_id: userId }).then(({ data: empId }) => {
          if (empId) {
            supabase
              .from('user_profiles')
              .update({ employee_record_id: empId, updated_at: new Date().toISOString() })
              .eq('id', userId)
              .then(() => {});
          }
        });
      }
      return data as UserProfile | null;
    } catch {
      return null;
    }
  }, [supabase]);

  const fetchPermissions = useCallback(async (userId: string, role: string): Promise<Set<string>> => {
    // Super admins have all permissions — skip DB query entirely
    if (role === 'super_admin') return new Set(['*']);

    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select(`
          role_id,
          roles!inner(
            name,
            role_permissions!inner(
              permissions!inner(
                module,
                resource,
                action
              )
            )
          )
        `)
        .eq('user_id', userId);

      if (error) return new Set();

      const permSet = new Set<string>();
      if (data) {
        for (const userRole of data) {
          const role = userRole.roles as any;
          if (role?.role_permissions) {
            for (const rp of role.role_permissions) {
              const perm = rp.permissions;
              if (perm?.module && perm?.resource && perm?.action) {
                permSet.add(`${perm.module}:${perm.resource}:${perm.action}`);
              }
            }
          }
        }
      }
      return permSet;
    } catch {
      return new Set();
    }
  }, [supabase]);

  useEffect(() => {
    let isMounted = true;

    // Use getUser() for secure initial auth check (validates JWT server-side)
    // Then subscribe to auth state changes for subsequent updates
    const initAuth = async () => {
      try {
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        if (!isMounted) return;

        if (currentUser) {
          // Get session for the session object (needed for session state)
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          setSession(currentSession);
          setUser(currentUser);
          if (!fetchingRef.current) {
            fetchingRef.current = true;
            const p = await fetchProfile(currentUser.id);
            const perms = await fetchPermissions(currentUser.id, p?.hrms_role ?? 'employee');
            if (isMounted) {
              setProfile(p);
              setPermissions(perms);
            }
            fetchingRef.current = false;
          }
        } else {
          setSession(null);
          setUser(null);
        }
      } catch {
        // silent
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!isMounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        if (fetchingRef.current) return; // Debounce rapid state changes
        fetchingRef.current = true;
        const p = await fetchProfile(session.user.id);
        const perms = await fetchPermissions(session.user.id, p?.hrms_role ?? 'employee');
        if (isMounted) {
          setProfile(p);
          setPermissions(perms);
        }
        fetchingRef.current = false;
      } else {
        setProfile(null);
        setPermissions(new Set());
      }
      if (isMounted) setLoading(false);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile, fetchPermissions, supabase.auth]);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    // Update last_login_at fire-and-forget — don't block sign-in
    if (data?.user) {
      supabase
        .from('user_profiles')
        .update({ last_login_at: new Date().toISOString() })
        .eq('id', data.user.id)
        .then(() => {});
    }
    router.refresh();
    return data;
  };

  const signUp = async (email: string, password: string, metadata: Record<string, any> = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.full_name || '',
          avatar_url: metadata?.avatar_url || '',
          hrms_role: metadata?.hrms_role || 'employee',
        },
        emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
    setPermissions(new Set());
    router.push('/login');
    router.refresh();
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback?next=/reset-password`,
    });
    if (error) throw error;
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    if (user) {
      supabase
        .from('user_profiles')
        .update({ must_change_password: false })
        .eq('id', user.id)
        .then(() => {});
    }
  };

  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at != null;
  };

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    return fetchProfile(user.id);
  };

  const refreshProfile = async () => {
    if (!user) return;
    const p = await fetchProfile(user.id);
    const perms = await fetchPermissions(user.id, p?.hrms_role ?? 'employee');
    setProfile(p);
    setPermissions(perms);
  };

  const hasRole = (roles: HrmsRole | HrmsRole[]): boolean => {
    if (!profile) return false;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(profile.hrms_role);
  };

  const canAccess = (minRole: HrmsRole): boolean => {
    if (!profile) return false;
    const userLevel = ROLE_HIERARCHY[profile.hrms_role] ?? 0;
    const requiredLevel = ROLE_HIERARCHY[minRole] ?? 0;
    return userLevel >= requiredLevel;
  };

  /**
   * Check if the user has a specific permission.
   * Super admins always have access regardless of assigned permissions.
   */
  const hasPermission = (module: string, resource: string, action: string): boolean => {
    if (!profile) return false;
    // Super admin bypasses permission checks
    if (profile.hrms_role === 'super_admin') return true;
    return permissions.has(`${module}:${resource}:${action}`);
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    permissions,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshProfile,
    hasRole,
    canAccess,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
